import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, ArrowLeft, Check } from "lucide-react";
import { Reveal, Kicker, SectionHeading } from "@/components/site/primitives";
import { FEATURE_PAGES, FEATURE_ORDER } from "@/featureData";

const SubNav = ({ current }) => (
  <div className="flex flex-wrap items-center gap-2">
    {FEATURE_ORDER.map((slug) => {
      const active = slug === current;
      return (
        <Link
          key={slug}
          to={`/features/${slug}`}
          data-testid={`subnav-${slug}`}
          className={`px-4 py-2 font-mono-p text-xs uppercase tracking-widest transition-colors ${
            active ? "bg-volt text-white" : "border border-slate-200 text-slate-500 hover:border-volt hover:text-volt"
          }`}
        >
          {FEATURE_PAGES[slug].label}
        </Link>
      );
    })}
  </div>
);

export const FeaturePage = ({ data, onBookDemo }) => (
  <main className="pt-28">
    <section className="relative overflow-hidden border-b border-slate-200 bg-white grain">
      <div className="pointer-events-none absolute inset-0 -z-10 bg-grid opacity-60" />
      <div className="mx-auto max-w-[1400px] px-6 py-16 lg:px-10 lg:py-24">
        <Link to="/" data-testid="feature-back-home" className="group mb-10 inline-flex items-center gap-2 font-mono-p text-xs uppercase tracking-widest text-slate-500 transition-colors hover:text-volt">
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" /> All features
        </Link>

        <SubNav current={data.slug} />

        <div className="mt-10 max-w-4xl">
          <Kicker>{data.eyebrow}</Kicker>
          <h1 className="mt-6 font-display text-4xl font-black leading-[0.95] text-slate-900 sm:text-6xl lg:text-7xl">
            {data.title}
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-500">{data.sub}</p>
          <button
            data-testid="feature-hero-book-demo"
            onClick={onBookDemo}
            className="group mt-9 inline-flex items-center gap-2 bg-volt px-7 py-4 font-display text-base font-bold text-white transition-transform hover:-translate-y-0.5"
          >
            Book a Demo
            <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.2} />
          </button>
        </div>

        {data.stats && (
          <div className="mt-14 grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200 sm:grid-cols-3">
            {data.stats.map((s) => (
              <div key={s.k} className="bg-white px-6 py-8">
                <div className="font-display text-4xl font-black text-volt">{s.v}</div>
                <div className="mt-2 font-mono-p text-[11px] uppercase tracking-widest text-slate-400">{s.k}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>

    {data.flow && (
      <section className="border-b border-slate-200 bg-[#F4F6FB]">
        <div className="mx-auto max-w-[1400px] px-6 py-20 lg:px-10">
          <Reveal>
            <Kicker>The workflow</Kicker>
            <h2 className="mt-5 font-display text-3xl font-extrabold text-slate-900 sm:text-4xl">From start to finish, guided.</h2>
          </Reveal>
          <div className="mt-12 flex flex-wrap items-center gap-y-4">
            {data.flow.map((step, i) => (
              <Reveal key={step} delay={i * 0.06}>
                <div className="flex items-center">
                  <div className="flex items-center gap-3 rounded-md border border-slate-200 bg-white px-5 py-4">
                    <span className="font-mono-p text-xs text-volt">{String(i + 1).padStart(2, "0")}</span>
                    <span className="font-display font-bold text-slate-900">{step}</span>
                  </div>
                  {i < data.flow.length - 1 && <ArrowRight className="mx-2 h-5 w-5 text-volt" />}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    )}

    <section className="mx-auto max-w-[1400px] px-6 py-20 lg:px-10 lg:py-28">
      <Reveal>
        <Kicker>Capabilities</Kicker>
        <SectionHeading className="mt-5">Built for {data.label.toLowerCase()}.</SectionHeading>
      </Reveal>
      <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {data.features.map((f, i) => (
          <Reveal key={f.title} delay={i * 0.05}>
            <motion.div
              whileHover={{ y: -6 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
              data-testid={`feature-card-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
              className="flex h-full flex-col rounded-lg border border-slate-200 bg-white p-6 transition-colors hover:border-volt/50 hover:shadow-[0_20px_50px_-30px_rgba(47,68,232,0.35)]"
            >
              <f.icon className="h-7 w-7 text-volt" strokeWidth={1.5} />
              <h3 className="mt-6 font-display text-xl font-bold text-slate-900">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-500">{f.desc}</p>
            </motion.div>
          </Reveal>
        ))}
      </div>
    </section>

    <section className="bg-volt text-white">
      <div className="mx-auto flex max-w-[1400px] flex-col items-start justify-between gap-8 px-6 py-16 lg:flex-row lg:items-center lg:px-10">
        <div>
          <div className="font-mono-p text-xs uppercase tracking-[0.3em] text-white/70">See it on your stock</div>
          <h2 className="mt-4 max-w-2xl font-display text-4xl font-black leading-[0.95] sm:text-5xl">
            Ready to take control of {data.label.toLowerCase()}?
          </h2>
        </div>
        <button
          data-testid="feature-cta-book-demo"
          onClick={onBookDemo}
          className="group flex shrink-0 items-center gap-3 bg-white px-8 py-5 font-display text-lg font-bold text-volt transition-transform hover:-translate-y-1"
        >
          Book a Demo
          <ArrowUpRight className="h-6 w-6 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={2.2} />
        </button>
      </div>
    </section>
  </main>
);

export { Check };
